import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

import { tpaColorName } from '../../utils/color';

import {
  FeaturedBadgeAlignment,
  FeaturedBadgePlacement,
  FeaturedBadgeStyle,
} from './constants';

export type FeaturedBadgeStylesParams = {
  featuredBadgeStyle: StyleParamType.Number;
  featuredBadgeTextFont: StyleParamType.Font;
  featuredBadgeTextColor: StyleParamType.Color;
  featuredBadgeBackgroundColor: StyleParamType.Color;
  featuredBadgeBorderColor: StyleParamType.Color;
  featuredBadgeBorderWidth: StyleParamType.Number;
  featuredBadgeBorderRadius: StyleParamType.Number;
  featuredBadgePlacement: StyleParamType.Number;
  featuredBadgeAlignment: StyleParamType.Number;
};

export type FeaturedItemStylesParams = {
  featuredItemTitleFont: StyleParamType.Font;
  featuredItemTitleColor: StyleParamType.Color;
  featuredItemDescriptionFont: StyleParamType.Font;
  featuredItemDescriptionColor: StyleParamType.Color;
  featuredItemPriceFont: StyleParamType.Font;
  featuredItemPriceColor: StyleParamType.Color;
  featuredItemBackgroundColor: StyleParamType.Color;
  featuredItemBorderTopColor: StyleParamType.Color;
  featuredItemBorderRightColor: StyleParamType.Color;
  featuredItemBorderBottomColor: StyleParamType.Color;
  featuredItemBorderLeftColor: StyleParamType.Color;
  featuredItemBorderTopWidth: StyleParamType.Number;
  featuredItemBorderRightWidth: StyleParamType.Number;
  featuredItemBorderBottomWidth: StyleParamType.Number;
  featuredItemBorderLeftWidth: StyleParamType.Number;
  featuredItemBorderLinked: StyleParamType.Number;
  featuredItemBorderRadiusTopLeft: StyleParamType.Number;
  featuredItemBorderRadiusTopRight: StyleParamType.Number;
  featuredItemBorderRadiusBottomLeft: StyleParamType.Number;
  featuredItemBorderRadiusBottomRight: StyleParamType.Number;
  featuredItemBorderRadiusLinked: StyleParamType.Number;
};

export type HighlightedItemStylesParams = FeaturedBadgeStylesParams &
  FeaturedItemStylesParams;

export const featuredBadgeStylesParams =
  createStylesParams<FeaturedBadgeStylesParams>({
    featuredBadgeStyle: {
      type: StyleParamType.Number,
      getDefaultValue: () => FeaturedBadgeStyle.FilledPill,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: {
          type: 'static',
          value: FeaturedBadgeStyle.FilledPill,
        },
        ai: {
          description:
            'Visual style preset of the featured badge: 1=Filled, 2=Outlined, 3=FilledPill, 4=OutlinedPill, 5=TextOnly',
        },
      },
    },
    featuredBadgeTextFont: {
      type: StyleParamType.Font,
      getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 14 }),
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description: 'Font of the featured badge text label on menu items',
        },
      },
    },
    featuredBadgeTextColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam(
        tpaColorName('wst-button-primary-color'),
        1,
      ),
      metadata: {
        type: StyleType.color,
        defaultValue: {
          type: 'static',
          preset: 'button-color-text-primary',
          opacity: 1,
        },
        ai: {
          description: 'Text color of the featured badge label on menu items',
        },
      },
    },
    featuredBadgeBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam(
        tpaColorName('wst-button-primary-background-color'),
        1,
      ),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: {
          type: 'static',
          preset: 'button-color-fill-primary',
          opacity: 1,
        },
        ai: {
          description:
            'Background fill color of the featured badge on menu items',
        },
      },
    },
    featuredBadgeBorderColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam(
        tpaColorName('wst-paragraph-2-color'),
        0.1,
      ),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: {
          type: 'static',
          preset: 'color-text-primary',
          opacity: 0.1,
        },
        ai: { description: 'Border color of the featured badge on menu items' },
      },
    },
    featuredBadgeBorderWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            'Border thickness (px) of the featured badge on menu items',
        },
      },
    },
    featuredBadgeBorderRadius: {
      type: StyleParamType.Number,
      getDefaultValue: () => 20,
      metadata: {
        type: StyleType.borderTopLeftRadius,
        defaultValue: { type: 'static', value: 20 },
        ai: {
          description: 'Corner radius (px) of the featured badge on menu items',
        },
      },
    },
    featuredBadgePlacement: {
      type: StyleParamType.Number,
      getDefaultValue: () => FeaturedBadgePlacement.ItemInfo,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: {
          type: 'static',
          value: FeaturedBadgePlacement.ItemInfo,
        },
        ai: {
          description:
            'Placement of the featured badge: 1=ItemInfo (text area), 2=OnImage',
        },
      },
    },
    featuredBadgeAlignment: {
      type: StyleParamType.Number,
      getDefaultValue: () => FeaturedBadgeAlignment.Start,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: {
          type: 'static',
          value: FeaturedBadgeAlignment.Start,
        },
        ai: {
          description:
            'Horizontal alignment of the featured badge when placed on image: 0=Start, 1=Center, 2=End',
        },
      },
    },
  });

export const featuredItemStylesParams =
  createStylesParams<FeaturedItemStylesParams>({
    featuredItemTitleFont: {
      type: StyleParamType.Font,
      getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 16 }),
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
        ai: { description: 'Font of the title for highlighted menu items' },
      },
    },
    featuredItemTitleColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Text color of the title for highlighted menu items',
        },
      },
    },
    featuredItemDescriptionFont: {
      type: StyleParamType.Font,
      getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 14 }),
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description: 'Font of the description for highlighted menu items',
        },
      },
    },
    featuredItemDescriptionColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Text color of the description for highlighted menu items',
        },
      },
    },
    featuredItemPriceFont: {
      type: StyleParamType.Font,
      getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 16 }),
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
        ai: { description: 'Font of the price for highlighted menu items' },
      },
    },
    featuredItemPriceColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Text color of the price for highlighted menu items',
        },
      },
    },
    featuredItemBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam(
        tpaColorName('wst-primary-background-color'),
        0,
      ),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: {
          type: 'static',
          preset: 'color-fill-background-primary',
          opacity: 0,
        },
        ai: { description: 'Background color of highlighted menu items' },
      },
    },
    featuredItemBorderTopColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0),
      metadata: {
        type: StyleType.borderTopColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 0 },
        ai: { description: 'Top border color of highlighted menu items' },
      },
    },
    featuredItemBorderRightColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0),
      metadata: {
        type: StyleType.borderRightColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 0 },
        ai: { description: 'Right border color of highlighted menu items' },
      },
    },
    featuredItemBorderBottomColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0),
      metadata: {
        type: StyleType.borderBottomColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 0 },
        ai: { description: 'Bottom border color of highlighted menu items' },
      },
    },
    featuredItemBorderLeftColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0),
      metadata: {
        type: StyleType.borderLeftColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 0 },
        ai: { description: 'Left border color of highlighted menu items' },
      },
    },
    featuredItemBorderTopWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Top border thickness (px) of highlighted menu items',
        },
      },
    },
    featuredItemBorderRightWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderRightWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Right border thickness (px) of highlighted menu items',
        },
      },
    },
    featuredItemBorderBottomWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Bottom border thickness (px) of highlighted menu items',
        },
      },
    },
    featuredItemBorderLeftWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderLeftWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Left border thickness (px) of highlighted menu items',
        },
      },
    },
    featuredItemBorderLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            '1 = all highlighted item border sides locked together, 0 = each side independent',
        },
      },
    },
    featuredItemBorderRadiusTopLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Top-left corner radius (px) of highlighted menu items',
        },
      },
    },
    featuredItemBorderRadiusTopRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Top-right corner radius (px) of highlighted menu items',
        },
      },
    },
    featuredItemBorderRadiusBottomLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-left corner radius (px) of highlighted menu items',
        },
      },
    },
    featuredItemBorderRadiusBottomRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-right corner radius (px) of highlighted menu items',
        },
      },
    },
    featuredItemBorderRadiusLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            '1 = all highlighted item corner radii locked together, 0 = each corner independent',
        },
      },
    },
  });

export const highlightedItemStylesParams =
  createStylesParams<HighlightedItemStylesParams>({
    ...featuredBadgeStylesParams,
    ...featuredItemStylesParams,
  });
